import { FC } from "react";

import { LightbulbIcon, ReplayIcon, SearchIcon } from "@/assets/icons";

import {
  AGENTIC_AI_RECENT_SEARCH_LABEL,
  AGENTIC_AI_RECENT_SEARCHES_DESCRIPTION,
  AGENTIC_AI_RECENT_SEARCHES_EMPTY,
  AGENTIC_AI_RECENT_SEARCHES_HEADING,
  AGENTIC_AI_SEARCH_NO_RESULTS,
  AGENTIC_AI_SEARCH_SUGGESTIONS,
  AGENTIC_AI_SEARCH_SUGGESTIONS_HEADING,
  AGENTIC_AI_SEARCH_TIPS_DESCRIPTION,
  AGENTIC_AI_SEARCH_TIPS_HEADING
} from "../constants";

type ChatSearchIdleProps = {
  recentSearches: string[];
  onPickRecent: (term: string) => void;
};

export const ChatSearchIdle: FC<ChatSearchIdleProps> = ({
  recentSearches,
  onPickRecent
}) => (
  <div className="flex flex-col gap-4">
    <section className="flex flex-col gap-3 rounded-xl border border-gainsboro bg-white p-4">
      <div>
        <h2 className="text-base font-semibold text-txtHeading">
          {AGENTIC_AI_RECENT_SEARCHES_HEADING}
        </h2>
        <p className="text-xs text-txtBody">
          {AGENTIC_AI_RECENT_SEARCHES_DESCRIPTION}
        </p>
      </div>
      {recentSearches.length === 0 ? (
        <p className="text-sm text-txtBody">
          {AGENTIC_AI_RECENT_SEARCHES_EMPTY}
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {recentSearches.map((term) => (
            <li key={term}>
              <button
                type="button"
                aria-label={`${AGENTIC_AI_RECENT_SEARCH_LABEL} ${term}`}
                onClick={() => onPickRecent(term)}
                className="flex w-full items-center gap-3 rounded-lg border border-gainsboro px-3 py-2.5 text-left hover:bg-teal-50"
              >
                <SearchIcon
                  aria-hidden
                  className="size-4 shrink-0 fill-dim-gray"
                />
                <span className="min-w-0 flex-1 truncate text-sm text-txtHeading">
                  {term}
                </span>
                <ReplayIcon aria-hidden className="size-4 shrink-0" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>

    <section className="flex items-center gap-5 rounded-xl bg-teal-50 p-3">
      <span
        aria-hidden
        className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-white"
      >
        <LightbulbIcon className="size-6" />
      </span>
      <div className="flex flex-col gap-1">
        <h2 className="text-base font-semibold text-teal-500">
          {AGENTIC_AI_SEARCH_TIPS_HEADING}
        </h2>
        <p className="text-xs text-txtBody">
          {AGENTIC_AI_SEARCH_TIPS_DESCRIPTION}
        </p>
      </div>
    </section>
  </div>
);

export const ChatSearchNoResults: FC<{ query: string }> = ({ query }) => (
  <section className="flex flex-col gap-4">
    <p className="break-words pb-2 text-sm text-txtBody">
      {`${AGENTIC_AI_SEARCH_NO_RESULTS} “${query}”`}
    </p>
    <p className="text-base font-semibold text-txtHeading">
      {AGENTIC_AI_SEARCH_SUGGESTIONS_HEADING}
    </p>
    <ul className="flex flex-col gap-5">
      {AGENTIC_AI_SEARCH_SUGGESTIONS.map(
        ({ icon: Icon, title, description }) => (
          <li key={title} className="flex items-center gap-3.5">
            <span
              aria-hidden
              className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-white"
            >
              <Icon className="size-[18px]" />
            </span>
            <div className="flex flex-col gap-0.5">
              <span className="text-base font-semibold text-txtHeading">
                {title}
              </span>
              <span className="text-xs text-txtBody">{description}</span>
            </div>
          </li>
        )
      )}
    </ul>
  </section>
);
